import React, { useState, useEffect, useRef } from 'react';
import { useNetwork } from '../contexts/NetworkContext';
import { useEscrow } from '../contexts/EscrowContext';
import { NETWORKS } from '../config/networks';
import './NetworkSelector.css';

const NetworkSelector: React.FC = () => {
  const { currentNetwork, supportedNetworks, switchNetwork, isLoading, error } = useNetwork();
  const { chainId: urlChainId, contractAddress, setContractAddress } = useEscrow();
  const [isOpen, setIsOpen] = useState(false);
  const syncAttemptedRef = useRef<string>('');
  const isSyncingRef = useRef(false);

  // Sync network with URL chainId
  useEffect(() => {
    // Only attempt sync if:
    // 1. We have a URL chainId
    // 2. Current network exists and doesn't match URL
    // 3. We haven't already attempted to sync this chainId
    // 4. We're not currently in the middle of a sync
    if (
      urlChainId && 
      currentNetwork && 
      currentNetwork.chainId.toString() !== urlChainId &&
      syncAttemptedRef.current !== urlChainId &&
      !isSyncingRef.current
    ) {
      const targetNetwork = supportedNetworks.find(n => n.chainId.toString() === urlChainId);
      if (targetNetwork) {
        const networkKey = Object.keys(NETWORKS).find(key => NETWORKS[key].chainId === targetNetwork.chainId);
        if (networkKey) {
          isSyncingRef.current = true;
          
          switchNetwork(networkKey)
            .then(() => {
              // Only mark as attempted after successful switch
              syncAttemptedRef.current = urlChainId;
            })
            .catch(err => {
              console.error('Failed to sync network with URL:', err);
              // Don't mark as attempted if it failed, allow retry on next render
            })
            .finally(() => {
              isSyncingRef.current = false;
            });
        }
      }
    }
  }, [urlChainId, currentNetwork, supportedNetworks, switchNetwork]);

  // Reset sync attempt when URL chainId changes to allow new sync attempts
  useEffect(() => {
    if (currentNetwork && currentNetwork.chainId.toString() === urlChainId) {
      syncAttemptedRef.current = urlChainId;
    }
  }, [urlChainId, currentNetwork]);

  const handleNetworkChange = async (networkKey: string) => {
    try {
      await switchNetwork(networkKey);
      setIsOpen(false);
      
      // Update URL with new chainId if contract address is set
      if (contractAddress) {
        const network = NETWORKS[networkKey];
        if (network) {
          setContractAddress(contractAddress, network.chainId.toString());
        }
      }
    } catch (err) {
      console.error('Failed to switch network:', err);
    }
  };

  if (supportedNetworks.length === 0) {
    return null;
  }

  return (
    <div className="network-selector">
      <button
        className="network-button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
      >
        <div className="network-info">
          <div className="network-name">
            {currentNetwork?.name || 'Select Network'}
          </div>
          <div className="network-chain-id">
            Chain ID: {currentNetwork?.chainId || 'N/A'}
          </div>
        </div>
        <div className={`network-arrow ${isOpen ? 'open' : ''}`}>
          ▼
        </div>
      </button>

      {isOpen && (
        <div className="network-dropdown">
          {supportedNetworks.map((network) => (
            <button
              key={network.chainId}
              className={`network-option ${
                currentNetwork?.chainId === network.chainId ? 'active' : ''
              }`}
              onClick={() => handleNetworkChange(
                Object.keys(NETWORKS).find(key => NETWORKS[key].chainId === network.chainId) || ''
              )}
            >
              <div className="network-option-info">
                <div className="network-option-name">{network.name}</div>
                <div className="network-option-details">
                  {network.nativeCurrency.symbol} • Chain ID: {network.chainId}
                </div>
              </div>
              {currentNetwork?.chainId === network.chainId && (
                <div className="network-check">✓</div>
              )}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="network-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default NetworkSelector;
